import { useAuthClient, useUserLogin } from '@gef-ui/authorization-v2';
import { getLabelMessage } from '@gef-ui/localization/messages';
import AngleDown from '@gef-ui/material-icons/AngleDown';
import ArrowRightFromBracketRegular from '@gef-ui/material-icons/ArrowRightFromBracketRegular';
import MoonRegular from '@gef-ui/material-icons/MoonRegular';
import SlidersUp from '@gef-ui/material-icons/SlidersUp';
import AvatarUniversal from '@gef-ui/material-illustrations-2023/avatar/AvatarUniversal';
import { useToggleThemeMode } from '@gef-ui/material-theme-utils/hooks';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useColorScheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import useIsMdAndUp from '../../../hooks/useIsMdAndUp';
import { useResponsiveContext } from '../../Appbar/ResponsiveContext';
import MenuPopover from '../../MenuPopover';
import { passSx, sxPropType, useDefaultComponentValue } from '../../utils';
import { AppbarUserContext } from '../AppbarUserContext';
const COMPONENT_NAME = 'AppbarUser';
const splitTakeFirstChar = (string, split)=>{
    const splitted = string.split(split);
    return splitted[splitted.length - 1].charAt(0).toUpperCase();
};
const AppbarUser = (inProps)=>{
    const props = useDefaultComponentValue({
        inProps,
        name: COMPONENT_NAME
    });
    const { mode } = useColorScheme();
    const { userName, userDescription, userPhotoSrc, handleLogout, children, darkModeToggle = true, showLogout = true, userSx, showIllustration } = props;
    const toggleThemeMode = useToggleThemeMode();
    const userLogin = useUserLogin();
    const authClient = useAuthClient();
    const resolvedUser = userName || userLogin;
    const avatar = useMemo(()=>{
        if (resolvedUser) {
            if (resolvedUser.includes('\\')) {
                return splitTakeFirstChar(resolvedUser, '\\');
            }
            if (resolvedUser.includes('\\\\')) {
                return splitTakeFirstChar(resolvedUser, '\\\\');
            }
            return resolvedUser.charAt(0).toUpperCase();
        }
    }, [
        resolvedUser
    ]);
    const resolvedLogout = handleLogout ? handleLogout : authClient?.handleLogout;
    const [open, setOpen] = useState(null);
    const responsiveContext = useResponsiveContext();
    const isMdAndUp = useIsMdAndUp();
    const handleOpen = (event)=>{
        setOpen(event.currentTarget);
    };
    const handleClose = ()=>{
        setOpen(null);
    };
    const resolvedAvatar = resolvedUser ? /*#__PURE__*/ React.createElement(Avatar, {
        sx: [
            !userPhotoSrc && showIllustration ? {
                overflow: 'visible',
                display: 'block',
                border: 'none'
            } : undefined,
            !isMdAndUp ? {
                mr: 0.5,
                width: 32,
                height: 32
            } : undefined
        ],
        src: userPhotoSrc
    }, !userPhotoSrc && showIllustration ? /*#__PURE__*/ React.createElement(AvatarUniversal, {
        width: isMdAndUp ? 44 : 36,
        height: isMdAndUp ? 40 : 32
    }) : avatar) : /*#__PURE__*/ React.createElement(Skeleton, {
        variant: "circular"
    }, /*#__PURE__*/ React.createElement(Avatar, {
        sx: !isMdAndUp ? {
            mr: 0.5,
            width: 32,
            height: 32
        } : undefined
    }));
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(ButtonBase, {
        onClick: handleOpen
    }, isMdAndUp ? resolvedAvatar : /*#__PURE__*/ React.createElement(Stack, {
        direction: "row",
        sx: {
            alignItems: 'center',
            gap: 1,
            p: 0.5,
            borderRadius: 3.75,
            backgroundColor: 'secondary.shades.50p'
        }
    }, /*#__PURE__*/ React.createElement(SlidersUp, {
        sx: {
            ml: 1
        }
    }), resolvedAvatar), isMdAndUp && /*#__PURE__*/ React.createElement(Stack, {
        direction: "row"
    }, /*#__PURE__*/ React.createElement(Stack, {
        sx: [
            {
                alignItems: 'flex-start',
                ml: 1
            },
            ...passSx(userSx)
        ]
    }, /*#__PURE__*/ React.createElement(Typography, {
        variant: "subtitle2",
        sx: (theme)=>({
                color: theme.palette.common.white
            })
    }, resolvedUser ? resolvedUser : /*#__PURE__*/ React.createElement(Skeleton, {
        width: 100
    })), /*#__PURE__*/ React.createElement(Typography, {
        variant: "body2",
        sx: (theme)=>({
                color: theme.palette.common.white,
                opacity: 0.5
            }),
        noWrap: true
    }, userDescription)), /*#__PURE__*/ React.createElement(AngleDown, {
        sx: {
            alignSelf: 'center'
        }
    }))), /*#__PURE__*/ React.createElement(MenuPopover, {
        arrow: "top-right",
        open: Boolean(open),
        anchorEl: open,
        onClose: handleClose,
        sx: {
            p: 0,
            mt: 1.5,
            ml: 0.75,
            '& .MuiMenuItem-root': {
                typography: 'body2',
                borderRadius: 0.75
            }
        }
    }, !isMdAndUp && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Box, {
        sx: [
            {
                my: 1.5,
                px: 2.5
            },
            ...passSx(userSx)
        ]
    }, /*#__PURE__*/ React.createElement(Typography, {
        variant: "subtitle2",
        noWrap: true
    }, resolvedUser), /*#__PURE__*/ React.createElement(Typography, {
        variant: "body2",
        sx: {
            color: 'text.secondary'
        },
        noWrap: true
    }, userDescription)), /*#__PURE__*/ React.createElement(Divider, null)), /*#__PURE__*/ React.createElement(AppbarUserContext.Provider, {
        value: {
            handleClose
        }
    }, /*#__PURE__*/ React.createElement(Stack, {
        sx: {
            p: 1
        }
    }, /*#__PURE__*/ React.createElement(MenuList, null, mode && darkModeToggle && /*#__PURE__*/ React.createElement(MenuItem, {
        disableRipple: true
    }, /*#__PURE__*/ React.createElement(ListItemIcon, null, /*#__PURE__*/ React.createElement(MoonRegular, null)), /*#__PURE__*/ React.createElement(ListItemText, {
        slotProps: {
            primary: {
                variant: 'body1'
            }
        }
    }, getLabelMessage('common:darkMode')), /*#__PURE__*/ React.createElement(Switch, {
        checked: mode === 'dark',
        onChange: toggleThemeMode
    })), mode && darkModeToggle && children && /*#__PURE__*/ React.createElement(Divider, null), children, !isMdAndUp && responsiveContext.length > 0 && /*#__PURE__*/ React.createElement(Divider, null), !isMdAndUp && responsiveContext, resolvedLogout && showLogout && /*#__PURE__*/ React.createElement(Divider, null), resolvedLogout && showLogout && /*#__PURE__*/ React.createElement(MenuItem, {
        disableRipple: true,
        onClick: resolvedLogout
    }, /*#__PURE__*/ React.createElement(ListItemIcon, null, /*#__PURE__*/ React.createElement(ArrowRightFromBracketRegular, null)), /*#__PURE__*/ React.createElement(ListItemText, {
        slotProps: {
            primary: {
                variant: 'body1'
            }
        }
    }, getLabelMessage('common:logout'))))))));
};
AppbarUser.propTypes = {
    children: PropTypes.node,
    darkModeToggle: PropTypes.bool,
    handleLogout: PropTypes.func,
    isDesktop: PropTypes.bool,
    showLogout: PropTypes.bool,
    userDescription: PropTypes.string,
    userName: PropTypes.string,
    userPhotoSrc: PropTypes.string,
    userSx: sxPropType,
    showIllustration: PropTypes.bool
};
export default AppbarUser;
